import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { NavigationEnd, Router } from '@angular/router';
import { marker } from '@common/translate-marker';
import { ModalService } from '@common/services/modal.service';
import { TranslateService } from '@ngx-translate/core';
import { BehaviorSubject, from, Observable } from 'rxjs';

export interface FormState {
    dirty: boolean;
    touched: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class FormSafeNavigationService {
    formState: BehaviorSubject<UntypedFormGroup[]> = new BehaviorSubject([]);

    constructor(
        private dialogService: ModalService,
        private translateService: TranslateService,
        private router: Router
    ) {
        router.events.subscribe((routerEvent) => {
            if (routerEvent instanceof NavigationEnd) {
                this.resetFormState();
            }
        });
    }

    addNewForm(newItem: UntypedFormGroup) {
        const current = this.formState.getValue() ?? [];
        this.formState.next([...current, newItem]);
    }

    resetFormState() {
        this.formState.next([]);
    }

    /**
     * When navigating away from a current component check if any of the forms have been touched
     * If so - display a pop-up to confirm/cancel navigation
     */
    verifyNoDirtyForms(): Observable<boolean> | boolean {
        const allForms = this.formState.getValue();

        if (allForms.length === 0) return true;

        const formsDirtyTouchedValues = allForms.map(
            (x) =>
                ({
                    dirty: x.dirty,
                    touched: x.touched
                }) as FormState
        );

        const containsTouched = formsDirtyTouchedValues.some((x) => x.touched);

        if (containsTouched) {
            const dialogResult = this.dialogService.confirm(
                this.translateService.instant(marker('Unsaved changes')),
                this.translateService.instant(marker('There are unsaved changes, you wish to proceed?')),
                this.translateService.instant(marker('Proceed')),
                this.translateService.instant(marker('Cancel'))
            );

            return from(dialogResult);
        }

        return true;
    }
}
